import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import { connectionsService } from '../services';
import toast from 'react-hot-toast';

const ConnectionsContext = createContext(null);

export const ConnectionsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { socket } = useSocket();

  // Connections state
  const [connections, setConnections] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [connectionRequests, setConnectionRequests] = useState([]);
  const [stats, setStats] = useState({ connections: 0, followers: 0, following: 0 });

  // Viewing user state
  const [viewingUserStats, setViewingUserStats] = useState({ connections: 0, followers: 0, following: 0 });
  const [isConnected, setIsConnected] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [connectionPending, setConnectionPending] = useState(false);

  // Fetch user's connections
  const fetchConnections = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await connectionsService.getConnections();
      setConnections(data);
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  }, [isAuthenticated]);

  // Fetch user's followers
  const fetchFollowers = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await connectionsService.getFollowers();
      setFollowers(data);
    } catch (error) {
      console.error('Error fetching followers:', error);
    }
  }, [isAuthenticated]);

  // Fetch who user is following
  const fetchFollowing = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await connectionsService.getFollowing();
      setFollowing(data);
    } catch (error) {
      console.error('Error fetching following:', error);
    }
  }, [isAuthenticated]);

  // Fetch connection requests
  const fetchConnectionRequests = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await connectionsService.getConnectionRequests();
      setConnectionRequests(data);
    } catch (error) {
      console.error('Error fetching connection requests:', error);
    }
  }, [isAuthenticated]);

  // Fetch user stats
  const fetchStats = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await connectionsService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [isAuthenticated]);

  // Send connection request
  const sendConnectionRequest = async (userId) => {
    try {
      await connectionsService.sendRequest(userId);
      toast.success('Connection request sent!');
      setConnectionPending(true);
      return true;
    } catch (error) {
      console.error('Error sending connection request:', error);
      toast.error('Failed to send connection request');
      return false;
    }
  };

  // Accept connection request
  const acceptConnectionRequest = async (requestId) => {
    try {
      await connectionsService.acceptRequest(requestId);
      toast.success('Connection accepted!');
      fetchConnectionRequests();
      fetchConnections();
      fetchStats();
      return true;
    } catch (error) {
      console.error('Error accepting connection:', error);
      toast.error('Failed to accept connection');
      return false;
    }
  };

  // Reject connection request
  const rejectConnectionRequest = async (requestId) => {
    try {
      await connectionsService.rejectRequest(requestId);
      toast.success('Connection request rejected');
      fetchConnectionRequests();
      return true;
    } catch (error) {
      console.error('Error rejecting connection:', error);
      toast.error('Failed to reject connection');
      return false;
    }
  };

  // Follow a user
  const followUser = async (userId) => {
    try {
      await connectionsService.followUser(userId);
      toast.success('Now following!');
      setIsFollowing(true);
      fetchFollowing();
      fetchStats();
      return true;
    } catch (error) {
      console.error('Error following user:', error);
      toast.error('Failed to follow user');
      return false;
    }
  };

  // Unfollow a user
  const unfollowUser = async (userId) => {
    try {
      await connectionsService.unfollowUser(userId);
      toast.success('Unfollowed');
      setIsFollowing(false);
      fetchFollowing();
      fetchStats();
      return true;
    } catch (error) {
      console.error('Error unfollowing user:', error);
      toast.error('Failed to unfollow user');
      return false;
    }
  };

  // Check connection status with a user
  const checkConnectionStatus = async (userId) => {
    try {
      const status = await connectionsService.checkConnectionStatus(userId);
      setIsConnected(status.isConnected);
      setConnectionPending(status.isPending);
      return status;
    } catch (error) {
      console.error('Error checking connection status:', error);
      return { isConnected: false, isPending: false };
    }
  };

  // Check follow status with a user
  const checkFollowStatus = async (userId) => {
    try {
      const status = await connectionsService.checkFollowStatus(userId);
      setIsFollowing(status.isFollowing);
      return status;
    } catch (error) {
      console.error('Error checking follow status:', error);
      return { isFollowing: false };
    }
  };

  // Fetch stats for a specific user
  const fetchUserStats = async (userId) => {
    try {
      const data = await connectionsService.getUserStats(userId);
      setViewingUserStats(data);
      return data;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return { connections: 0, followers: 0, following: 0 };
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (isAuthenticated) {
      fetchConnections();
      fetchFollowers();
      fetchFollowing();
      fetchConnectionRequests();
      fetchStats();
    }
  }, [isAuthenticated, fetchConnections, fetchFollowers, fetchFollowing, fetchConnectionRequests, fetchStats]);

  // Listen for socket events
  useEffect(() => {
    if (socket) {
      socket.on('connection_request', (data) => {
        toast.success(`${data.user.full_name} sent you a connection request`);
        fetchConnectionRequests();
      });

      socket.on('connection_accepted', (data) => {
        toast.success(`${data.user.full_name} accepted your connection request`);
        fetchConnections();
        fetchStats();
      });

      socket.on('new_follower', (data) => {
        toast.success(`${data.user.full_name} started following you`);
        fetchFollowers();
        fetchStats();
      });

      return () => {
        socket.off('connection_request');
        socket.off('connection_accepted');
        socket.off('new_follower');
      };
    }
  }, [socket, fetchConnectionRequests, fetchConnections, fetchFollowers, fetchStats]);

  const value = {
    // State
    connections,
    followers,
    following,
    connectionRequests,
    stats,
    viewingUserStats,
    isConnected,
    isFollowing,
    connectionPending,

    // Actions
    sendConnectionRequest,
    acceptConnectionRequest,
    rejectConnectionRequest,
    followUser,
    unfollowUser,
    checkConnectionStatus,
    checkFollowStatus,
    fetchUserStats,

    // Refresh functions
    refreshConnections: fetchConnections,
    refreshFollowers: fetchFollowers,
    refreshFollowing: fetchFollowing,
    refreshRequests: fetchConnectionRequests,
    refreshStats: fetchStats,
  };

  return (
    <ConnectionsContext.Provider value={value}>
      {children}
    </ConnectionsContext.Provider>
  );
};

export const useConnections = () => {
  const context = useContext(ConnectionsContext);
  if (!context) {
    throw new Error('useConnections must be used within ConnectionsProvider');
  }
  return context;
};

export default ConnectionsContext;
